import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import dashboardService from 'ApiService/Admin/dashboardService';
import Card from 'components/card';
import BarChart from 'components/charts/BarChart';
import { AuthContext } from 'contexts/AuthContext';
import BatchPlacementChart from './BatchPlacementChart';
import InputField from 'components/fields/InputField';

const getCurrentAcademicYear = () => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  return currentYear;
};

const generateAcademicYears = (currentYear, range = 5) => {
  const years = [];
  for (let i = -range; i <= range; i++) {
    years.push(currentYear + i);
  }
  return years;
};

const PlacementChart = () => {
  const currentAcademicYear = getCurrentAcademicYear();
  const years = generateAcademicYears(currentAcademicYear);

  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(currentAcademicYear);
  const [summaryData, setSummaryData] = useState({
    totalCourses: 0,
    totalStudents: 0,
    totalPlacedStudents: 0,
    overallPlacementPercentage: 0,
  });
  const [placementChartData, setPlacementChartData] = useState([]);
  const [barChartDataPlacements, setBarChartDataPlacements] = useState([]);
  const [xAxisCategories, setXAxisCategories] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isCardVisible, setIsCardVisible] = useState(true); // Controls visibility of bar chart card
  const [isBatchChartVisible, setIsBatchChartVisible] = useState(false); // Controls visibility of BatchPlacementChart
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  const { state } = useContext(AuthContext);

  const selectedBranch = state?.selectedBranch;

  const getData = async (start = startDate, end = endDate) => {
    setLoading(true);
    try {
      const response = await dashboardService.getPlacementDataForChart(
        selectedBranch,
        start,
        end
      );

      if (response.data.success) {
        const { placementChartData, summary } = response.data;

        setPlacementChartData(placementChartData);
        setSummaryData({
          totalCourses: summary.totalCourses,
          totalStudents: summary.totalStudents,
          totalPlacedStudents: summary.totalPlacedStudents,
          overallPlacementPercentage: summary.overallPlacementPercentage,
        });

        const totalStudentsData = placementChartData.map(
          (data) => data.totalStudents
        );
        const placedStudentsData = placementChartData.map(
          (data) => data.placedStudents
        );
        const courseNames = placementChartData.map((data) => data.courseName);

        setBarChartDataPlacements([
          { name: 'Total Students', data: totalStudentsData },
          { name: 'Placed Students', data: placedStudentsData },
        ]);
        setXAxisCategories(courseNames);
      } else {
        toast.error('Failed to fetch placement data');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [state?.user?.userId, selectedYear, selectedBranch]);

  const handleBarClick = (dataPointIndex) => {
    const selectedCourseData = placementChartData.find(
      (data) => data.courseName === xAxisCategories[dataPointIndex]
    );

    if (selectedCourseData) {
      setSelectedCourse({
        courseId: selectedCourseData.courseId,
        courseName: selectedCourseData.courseName,
        startDate: startDate,
        endDate: endDate,
      });
      setIsCardVisible(false);
      setIsBatchChartVisible(true);
    }
  };

  const handleBatchChartClose = () => {
    setIsBatchChartVisible(false);
    setIsCardVisible(true);
    setSelectedCourse(null);
  };

  const barChartOptionsPlacements = {
    chart: {
      toolbar: { show: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          handleBarClick(config.dataPointIndex);
        },
      },
    },
    tooltip: { theme: 'dark' },
    xaxis: {
      categories: xAxisCategories,
      labels: {
        show: true,
        style: {
          colors: '#A3AED0',
          fontSize: '12px',
          fontWeight: '500',
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      show: false,
      labels: {
        show: true,
        style: {
          colors: '#CBD5E0',
          fontSize: '14px',
        },
      },
    },
    grid: {
      show: false,
      strokeDashArray: 5,
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
    },
    fill: {
      type: 'gradient',
      gradient: {
        type: 'vertical',
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        colorStops: [
          [
            {
              offset: 0,
              color: 'var(--color-500)',
              opacity: 1,
            },
            {
              offset: 100,
              color: '#6F32E0',
              opacity: 0.0,
            },
          ],
          [
            {
              offset: 0,
              color: '#36A2EB',
              opacity: 1,
            },
            {
              offset: 100,
              color: 'rgba(255, 255, 255, 1)',
              opacity: 0.0,
            },
          ],
        ],
      },
    },
    dataLabels: { enabled: false },
    plotOptions: {
      bar: {
        borderRadius: 5,
        columnWidth: '20px',
      },
    },
    legend: {
      markers: {
        fillColors: ['var(--color-500)', '#36A2EB'],
      },
      labels: {
        colors: 'dark:#CBD5E0',
      },
    },
  };

  return (
    <div>
      {isCardVisible && (
        <Card extra="pb-7 p-4" style={{ border: '3px solid var(--color-500)' }}>
          <div className="mb-4 flex flex-col items-end gap-4 md:flex-row">
            <div>
              <InputField
                label="Start Date"
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>
            <div>
              <InputField
                label="End Date"
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setStartDate(tempStartDate);
                  setEndDate(tempEndDate);
                  getData(tempStartDate, tempEndDate);
                }}
                className="rounded-md bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
              >
                Apply
              </button>
              <button
                onClick={() => {
                  setTempStartDate('');
                  setTempEndDate('');
                  setStartDate('');
                  setEndDate('');
                  getData('', '');
                }}
                className="text-black rounded-md bg-gray-600 px-4 py-2 transition hover:bg-gray-800"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="flex flex-col items-start justify-between lg:flex-row lg:items-center">
            <div className="mb-4 lg:mb-0 lg:mr-4">
              <p className="text-sm font-medium text-gray-600">
                Placement Overview
              </p>
              <div className="flex flex-wrap gap-4">
                <p className="text-l font-bold text-navy-700 dark:text-white">
                  Total Courses: {summaryData.totalCourses.toLocaleString()}
                </p>
                <p className="text-l font-bold text-green-600">
                  Total Students: {summaryData.totalStudents.toLocaleString()}
                </p>
                <p className="text-l font-bold text-blue-600">
                  Placed Students:{' '}
                  {summaryData.totalPlacedStudents.toLocaleString()}
                </p>
                <p className="text-l font-bold text-blue-600">
                  Remaining Students:{' '}
                  {summaryData.totalStudents.toLocaleString() -
                    summaryData.totalPlacedStudents.toLocaleString()}
                </p>
                <p className="text-l font-bold text-purple-600">
                  Overall Placement :{' '}
                  {summaryData.overallPlacementPercentage.toFixed(2)}%
                </p>
              </div>
            </div>
          </div>

          <div className="h-[300px] w-full pb-0 pt-10">
            {loading ? (
              <div>Loading...</div>
            ) : (
              <BarChart
                chartData={barChartDataPlacements}
                chartOptions={barChartOptionsPlacements}
              />
            )}
          </div>
        </Card>
      )}

      {isBatchChartVisible && selectedCourse && (
        <BatchPlacementChart
          selectedCourse={selectedCourse}
          onClose={handleBatchChartClose}
        />
      )}
    </div>
  );
};

export default PlacementChart;
